import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DownloadIcon, LoggerService, PlusIcon, RootState, UploadIcon, UUID } from '@umanforge/common';
import type { ChartWithDataApi } from 'interfaces/chartWithData.interface';
import { ToastService } from 'services/toast.service';
import { ChartPingService } from 'services/chartPing.service';
import ChartValueModal from './chartValueModal.component';
import { ChartValueService } from 'services/chartValue.service';
import { useDispatch, useSelector } from 'react-redux';
import { ChartOfSectionApi } from 'interfaces/chartOfSection.interface';
import ImportChartValueModal from './ImportChartValueModal.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';

const logger = new LoggerService( 'ChartSectionsStatus' );

const SLIDE_STYLES = `
  @keyframes chart-slide-from-right {
    from { opacity: 0; transform: translateX(60px); }
    to   { opacity: 1; transform: translateX(0);    }
  }
  @keyframes chart-slide-from-left {
    from { opacity: 0; transform: translateX(-60px); }
    to   { opacity: 1; transform: translateX(0);     }
  }
  .chart-slide-right { animation: chart-slide-from-right 0.28s cubic-bezier(0.25,0.46,0.45,0.94) both; }
  .chart-slide-left  { animation: chart-slide-from-left  0.28s cubic-bezier(0.25,0.46,0.45,0.94) both; }
`;

const arrowBtnBase = [
  'p-2 rounded-full transition-all duration-150',
  'hover:bg-blue-50 hover:shadow-md hover:shadow-blue-200',
  'active:scale-75 active:bg-blue-100',
].join( ' ' );

const ArrowLeft: React.FC = () => {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="transition-transform duration-150 group-hover:-translate-x-0.5">
      <path d="M15 6l-6 6 6 6" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const ArrowRight: React.FC = () => {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="transition-transform duration-150 group-hover:translate-x-0.5">
      <path d="M9 6l6 6-6 6" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

type ChartSectionsStatusProps = {
  isLoadingSections: boolean;
  hasSections: boolean;
  selectedSectionName: string;
  selectedSectionId?: string | null;
  charts?: React.ReactNode[];
  chartsOfSection?: ChartOfSectionApi[];
  chartManagerIds?: UUID[];
  currentUserId?: UUID;
  isAdminOfEventManager?: boolean;
  onRefresh?: () => void;
};

const MAX_GRAPHS = 6;
const PAGE_SIZE_WIDE = 2;
const PAGE_SIZE_NARROW = 1;
const isWide = ( containerWidth: number ) => { return containerWidth >= 560; };



const ChartSectionsStatus: React.FC<ChartSectionsStatusProps> = ( {
  isLoadingSections,
  hasSections,
  selectedSectionName,
  selectedSectionId,
  charts = [],
  chartsOfSection = [],
  chartManagerIds = [],
  currentUserId,
  isAdminOfEventManager = false,
  onRefresh,
} ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const toastService = new ToastService( 'ChartSectionsStatus' );

  const containerRef = useRef<HTMLDivElement>( null );
  const roRef = useRef<ResizeObserver | null>( null );
  const rafRef = useRef<number | null>( null );

  const [ page, setPage ] = useState( 0 );
  const [ addValueModalChartInfo, setAddValueModalChartInfo ] = useState<{ id: UUID; readStart: string } | null>( null );
  const [ importModalChartInfo, setImportModalChartInfo ] = useState<ChartOfSectionApi | null>( null );
  const [ slideClass, setSlideClass ] = useState( 'chart-slide-right' );
  const [ animKey, setAnimKey ] = useState( 0 );
  const [ pageSize, setPageSize ] = useState( () => { return isWide( window.innerWidth ) ? PAGE_SIZE_WIDE : PAGE_SIZE_NARROW; } );
  const [ resetModalChartInfoId, setResetModalChartInfoId ] = useState<UUID | null>( null );

  /* ── Reset page when SECTION changes (not on data refresh) ── */
  useEffect( () => {
    setPage( 0 );
  }, [ selectedSectionId ] );

  /* ── Callback ref : installe le ResizeObserver dès que le div est dans le DOM ── */
  const setContainerRef = useCallback( ( el: HTMLDivElement | null ) => {
    /* Cleanup previous */
    if ( roRef.current ) { roRef.current.disconnect(); roRef.current = null; }
    if ( rafRef.current !== null ) { cancelAnimationFrame( rafRef.current ); rafRef.current = null; }
    ( containerRef as React.MutableRefObject<HTMLDivElement | null> ).current = el;
    if ( !el ) { return; }

    const updatePageSize = () => {
      const width = el.offsetWidth;
      if ( width === 0 ) { return; }
      const next = isWide( width ) ? PAGE_SIZE_WIDE : PAGE_SIZE_NARROW;
      setPageSize( prev => { if ( prev !== next ) { setPage( 0 ); } return next; } );
    };

    updatePageSize();

    if ( typeof ResizeObserver === 'undefined' ) { return; }
    const ro = new ResizeObserver( () => {
      if ( rafRef.current !== null ) { cancelAnimationFrame( rafRef.current ); }
      rafRef.current = requestAnimationFrame( () => { updatePageSize(); } );
    } );
    ro.observe( el );
    roRef.current = ro;
  }, [] );

  const goLeft = () => {
    setSlideClass( 'chart-slide-left' );
    setAnimKey( k => { return k + 1; } );
    setPage( page - 1 );
  };

  const goRight = () => {
    setSlideClass( 'chart-slide-right' );
    setAnimKey( k => { return k + 1; } );
    setPage( page + 1 );
  };

  try {
    if ( isLoadingSections ) {
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="px-3 py-1 rounded-full bg-slate-100 text-xs font-medium text-slate-500 border border-slate-200">
            { t( 'chart_sections_loading' ) }
          </p>
        </div>
      );
    }

    if ( !hasSections ) {
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="px-3 py-1 rounded-full bg-slate-100 text-xs font-medium text-slate-500 border border-dashed border-slate-300">
            { t( 'chart_sections_no_section' ) }
          </p>
        </div>
      );
    }

    const clampedCharts = charts.slice( 0, MAX_GRAPHS );
    const totalPages = Math.ceil( clampedCharts.length / pageSize );
    const canGoLeft = page > 0;
    const canGoRight = page < totalPages - 1;
    const start = page * pageSize;
    const end = start + pageSize;
    const visibleCharts = clampedCharts.slice( start, end );

    return (
      <>
        <style>{ SLIDE_STYLES }</style>
        <div className="w-full" ref={ setContainerRef }>
          <div className="flex items-center justify-between border-b-2 border-blue-500 pb-1 pl-1 mb-4">
            <span className="text-black text-base sm:text-lg font-semibold truncate">{ selectedSectionName }</span>
          </div>
          <div className="flex items-center justify-between">
            { canGoLeft ? (
              <button
                className={ `group ${arrowBtnBase}` }
                onClick={ goLeft }
                aria-label="Précédent"
              >
                <ArrowLeft />
              </button>
            ) : <div className="w-10" /> }

            <div key={ animKey } className={ `flex-1 flex gap-4 justify-center ${slideClass}` }>
              { visibleCharts.length > 0 ? (
                <>
                  { visibleCharts.map( ( chart, idx ) => {
                    const absoluteIdx = start + idx;
                    const chartInfo = chartsOfSection[ absoluteIdx ] ?? null;
                    return (
                      <div key={ idx } className="@container bg-white rounded-lg shadow p-3 flex-1 min-w-0 flex flex-col items-center justify-center gap-2">
                        <div className="flex-1 flex items-center justify-center w-full">
                          { chart }
                        </div>
                        { chartInfo && ( isAdminOfEventManager || currentUserId === chartManagerIds[ absoluteIdx ] ) && (
                          chartInfo.type !== 'auto_increment_gauge' ? (
                            <div className='flex flex-col @md:flex-row gap-1 text-xs w-full'>
                              <Button
                                label={ t( 'translation:chart_sections_add_edit_value' ) }
                                icon={ <PlusIcon className='p-1'/> }
                                className="w-full px-3 py-0 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition"
                                onClick={ () => { setAddValueModalChartInfo( { id: chartInfo.id, readStart: chartInfo.readStart } ); } }
                              />
                              <div className='flex gap-1 '>
                                <Button
                                  label={ t( 'common:import' ) }
                                  icon={ <UploadIcon className='p-1.5'/> }
                                  className='w-full px-3 py-0 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition'
                                  onClick={ () => { setImportModalChartInfo( chartInfo ); } }
                                />
                                <Button
                                  label={ t( 'common:export' ) }
                                  icon={ <DownloadIcon className='p-1.5'/> }
                                  className='w-full px-3 py-0 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition'
                                  onClick={ () => { ChartValueService.exportChartValue( chartInfo, token, dispatch ); } }
                                />
                              </div>
                            </div>
                          ) : (
                            <div className='flex flex-col @md:flex-row gap-1 text-xs w-full'>
                              { ( () => {
                                let latestVal = 0;
                                try {
                                  const datas = ( chartInfo as ChartWithDataApi ).datas ?? {};
                                  const keys = Object.keys( datas ).sort();
                                  const last = keys[ keys.length - 1 ];
                                  latestVal = last != null ? ( datas[ last ]?.value ?? 0 ) : 0;
                                } catch ( e ) {
                                  latestVal = 0;
                                  console.error( 'Error getting latest value for auto_increment_gauge chart', e );
                                }
                                return (
                                  <Button
                                    label={ t( 'translation:chart_reset_counter', { defaultValue: 'Réinitialiser compteur' } ) }
                                    icon={ <PlusIcon className='p-1'/> }
                                    className="w-full px-3 py-0 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition"
                                    disable={ latestVal === 0 }
                                    onClick={ () => { setResetModalChartInfoId( chartInfo.id ); } }
                                  />
                                );
                              } )() }
                            </div>
                          )
                        ) }
                      </div>
                    );
                  } ) }
                  { visibleCharts.length < pageSize && pageSize > 1 && (
                    <div className="flex-1 min-w-0" />
                  ) }
                </>
              ) : (
                <div className="text-slate-400 italic">{ t( 'chart_sections_no_chart' ) }</div>
              ) }
            </div>

            { canGoRight ? (
              <button
                className={ `group ${arrowBtnBase}` }
                onClick={ goRight }
                aria-label="Suivant"
              >
                <ArrowRight />
              </button>
            ) : <div className="w-10" /> }
          </div>
        </div>
        { addValueModalChartInfo && (
          <ChartValueModal
            isOpen={ addValueModalChartInfo != null }
            onClose={ () => { setAddValueModalChartInfo( null ); } }
            chartInfoId={ addValueModalChartInfo.id }
            readStart={ addValueModalChartInfo.readStart }
            onSaved={ onRefresh }
          />
        ) }
        { importModalChartInfo && (
          <ImportChartValueModal
            isOpen={ importModalChartInfo != null }
            onClose={ () => { setImportModalChartInfo( null ); } }
            chartInfo={ importModalChartInfo }
            onSaved={ onRefresh }
          />
        ) }
        <ConfirmationModal
          title={ t( 'translation:confirm_reset_counter_title' ) }
          description={ t( 'translation:confirm_reset_counter_message' ) }
          isOpen={ resetModalChartInfoId != null }
          onCancel={ () => { setResetModalChartInfoId( null ); } }
          onConfirm={ async () => {
            if ( !resetModalChartInfoId ) { return; }
            try {
              const ok = await ChartPingService.postPing( resetModalChartInfoId, token, dispatch );
              if ( ok ) {
                toastService.showSuccess( t( 'translation:chart_reset_success', { defaultValue: 'Compteur réinitialisé' } ) );
                onRefresh?.();
                setResetModalChartInfoId( null );
              } else {
                toastService.showError( t( 'translation:chart_reset_error', { defaultValue: 'Erreur lors de la réinitialisation' } ) );
              }
            } catch ( err ) {
              toastService.showError( t( 'translation:chart_reset_error', { error: err, defaultValue: 'Erreur lors de la réinitialisation' } ) );
            }
          } }
        />
      </>
    );
  } catch ( err ) {
    logger.error( 'Error rendering ChartSectionsStatus', err );
    return (
      <div className="flex items-center justify-center">
        <p className="px-3 py-1 rounded-full bg-red-100 text-xs font-medium text-red-500 border border-red-200">
          { t( 'chart_sections_error' ) }
        </p>
      </div>
    );
  }
};

export default ChartSectionsStatus;
